import {
    type ApprovedWhatsAppTemplateParameters,
    type TemplateVariableSource,
    WHATSAPP_TEMPLATE_BUTTON_TYPES,
    WHATSAPP_TEMPLATE_PARAMETER_KINDS,
    WHATSAPP_TEMPLATE_VARIABLE_SOURCES,
} from 'cpool-constants';

import type { WhatsAppTemplate } from '~/features/whatsapp/hooks/useWhatsAppTemplates';

export const namedVariablesOf = (
    slot: ApprovedWhatsAppTemplateParameters | undefined,
): Record<string, TemplateVariableSource> =>
    slot?.kind === WHATSAPP_TEMPLATE_PARAMETER_KINDS.NAMED ? slot.variables : {};

// COMPUTED (trustpilot_link, dokument_upload_link) also needs the chat-matched applicant on the
// backend — mirrors the same check in `deriveTemplateParameters`.
const slotNeedsApplicant = (
    variables: Record<string, TemplateVariableSource>,
    namedValues?: Record<string, string>,
): boolean =>
    Object.entries(variables).some(
        ([name, variable]) =>
            (variable.source === WHATSAPP_TEMPLATE_VARIABLE_SOURCES.APPLICANT && !namedValues?.[name]?.trim()) ||
            variable.source === WHATSAPP_TEMPLATE_VARIABLE_SOURCES.COMPUTED,
    );

// A dynamic-URL button unconditionally requires loanRequestId on the backend — mirrors resolveTemplateButtonParameters.
const buttonsNeedApplicant = (buttons: WhatsAppTemplate['buttons'], language: string | undefined): boolean =>
    Boolean(
        language &&
            buttons?.[language]?.some(
                (button) => button.type === WHATSAPP_TEMPLATE_BUTTON_TYPES.URL && button.hasDynamicSuffix,
            ),
    );

type TemplateNeedsApplicantParams = {
    template: WhatsAppTemplate | undefined;
    namedValues?: Record<string, string>;
    headerNamedValues?: Record<string, string>;
    language?: string;
};

// Body and header are checked against their own slot's overrides — a name shared between them can
// diverge, so a union keyed by name alone would lose one slot's override.
export const templateNeedsApplicant = ({
    headerNamedValues,
    language,
    namedValues,
    template,
}: TemplateNeedsApplicantParams): boolean =>
    slotNeedsApplicant(namedVariablesOf(template?.parameters), namedValues) ||
    slotNeedsApplicant(namedVariablesOf(template?.headerParameters), headerNamedValues) ||
    buttonsNeedApplicant(template?.buttons, language);

const slotNeedsPartner = (
    variables: Record<string, TemplateVariableSource>,
    namedValues?: Record<string, string>,
): boolean =>
    Object.entries(variables).some(
        ([name, variable]) =>
            variable.source === WHATSAPP_TEMPLATE_VARIABLE_SOURCES.PARTNER && !namedValues?.[name]?.trim(),
    );

// Mirrors the `needsPartner` check in `deriveTemplateParameters` — the chat must have a linked partner.
export const templateNeedsPartner = ({
    headerNamedValues,
    namedValues,
    template,
}: Omit<TemplateNeedsApplicantParams, 'language'>): boolean =>
    slotNeedsPartner(namedVariablesOf(template?.parameters), namedValues) ||
    slotNeedsPartner(namedVariablesOf(template?.headerParameters), headerNamedValues);
